import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import moment from "moment";
import notifications from "../../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { canUseOrders } from "../../helpers/orderEnabled";
import {
  addOrderNote,
  createOrder,
  deleteOrder,
  duplicateOrder,
  getOrder,
  notifyOrderClient,
  searchOrderCustomers,
  searchOrderProducts,
  sendOrderConfirmation,
  updateOrder,
  updateOrderStatus,
  uploadOrderDocument,
} from "../../services/studioOrderApi";
import { parseGeoPath, unwrapGeoPayload } from "../gi-registry/geoPath";
import { WORKFLOW_STATUSES, newColourLine, workflowLabel } from "./orderStatus";
import {
  isOrderAudioNote,
  mapSearchRowToCustomer,
  pickAudioRecorderMime,
  shouldOfferCreateParty,
  type OrderCustomerOption,
} from "./orderParty";
import "../gi-registry/gi-registry.scss";
import "./orders.scss";
import OrderApprovalPanel from "./approval/OrderApprovalPanel";
import OrderMemosPanel from "./OrderMemosPanel";
import type { OrderAdvancePayment } from "../../services/orderApprovalApi";

type OrderDoc = {
  id?: string;
  url: string;
  fileName?: string;
  contentType?: string;
  uploadedAt?: string;
};

type OrderNote = {
  id?: string;
  message?: string;
  authorName?: string;
  createdAt?: string;
};

type ColourLine = {
  id: string;
  colour: string;
  quantity: string;
  workflowStatus: string;
  stuckReason: string;
  customStageLabel: string;
};

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const dateInputValue = (value: any) => {
  if (!value) {
    return "";
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const linesFromOrder = (data: any): ColourLine[] => {
  if (Array.isArray(data?.lines) && data.lines.length) {
    return data.lines.map((line: any) => ({
      id: line?.id || newColourLine().id,
      colour: line?.colour || "",
      quantity: line?.quantity != null ? String(line.quantity) : "",
      workflowStatus: line?.workflowStatus || "RECORDED",
      stuckReason: line?.stuckReason || "",
      customStageLabel: line?.customStageLabel || "",
    }));
  }
  if (data?.colour || data?.quantity != null) {
    return [
      {
        ...newColourLine(),
        colour: data.colour || "",
        quantity: data.quantity != null ? String(data.quantity) : "",
      },
    ];
  }
  return [newColourLine()];
};

const OrderForm = () => {
  const history = useHistory();
  const location = useLocation();
  const path = parseGeoPath(location.pathname, "orders");
  const orderId = path.id;
  const isNew = path.isNew;
  const isView = path.isView;
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canWrite = canUseOrders(role) && !isSuperAdmin;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteInFlight = useRef(false);
  const [error, setError] = useState("");
  const [orderCode, setOrderCode] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [partyOrganizationId, setPartyOrganizationId] = useState("");
  const [customerMatches, setCustomerMatches] = useState<OrderCustomerOption[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [productDesign, setProductDesign] = useState("");
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [specifications, setSpecifications] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState("RECORDED");
  const [stuckReason, setStuckReason] = useState("");
  const [customStageLabel, setCustomStageLabel] = useState("");
  const [lines, setLines] = useState<ColourLine[]>([newColourLine()]);
  const [documents, setDocuments] = useState<OrderDoc[]>([]);
  // Documents as last saved: only these exist on the server, so only these can go on an approval link.
  const [savedDocuments, setSavedDocuments] = useState<OrderDoc[]>([]);
  const [advancePayment, setAdvancePayment] = useState<OrderAdvancePayment | null>(null);
  const [communications, setCommunications] = useState<OrderNote[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [orgName, setOrgName] = useState("");
  const [traceStageLabel, setTraceStageLabel] = useState("Enquiry / order");
  const [completenessPercent, setCompletenessPercent] = useState(0);
  const [entryStarted, setEntryStarted] = useState(false);
  const [entryComplete, setEntryComplete] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [reminderCount, setReminderCount] = useState(0);
  const [placedAt, setPlacedAt] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [elapsedDays, setElapsedDays] = useState(0);
  const [allLinesCompleted, setAllLinesCompleted] = useState(false);
  const [clientConfirmationLabel, setClientConfirmationLabel] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOrderId, setConfirmOrderId] = useState("");
  const [confirmEmailDraft, setConfirmEmailDraft] = useState("");
  const [matchQuery, setMatchQuery] = useState("");
  const [matchResults, setMatchResults] = useState<any[]>([]);
  const [matching, setMatching] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Bumped on every order load or save; the approval panel reloads with it.
  const [orderVersion, setOrderVersion] = useState(0);

  const applyOrder = (data: any) => {
    setOrderVersion((v) => v + 1);
    setOrderCode(data.orderCode || "");
    setCustomerName(data.customerName || "");
    setCustomerPhone(data.customerPhone || "");
    setCustomerEmail(data.customerEmail || "");
    setPartyOrganizationId(data.partyOrganizationId || "");
    setProductDesign(data.productDesign || "");
    setProductId(data.productId || "");
    setProductName(data.productName || "");
    setDueDate(dateInputValue(data.dueDate));
    setSpecifications(data.specifications || "");
    setWorkflowStatus(data.workflowStatus || "RECORDED");
    setStuckReason(data.stuckReason || "");
    setCustomStageLabel(data.customStageLabel || "");
    setLines(linesFromOrder(data));
    setDocuments(Array.isArray(data.documents) ? data.documents : []);
    setSavedDocuments(Array.isArray(data.documents) ? data.documents : []);
    setAdvancePayment(data.advancePayment || null);
    setCommunications(Array.isArray(data.communications) ? data.communications : []);
    setOrgName(data.orgName || "");
    setTraceStageLabel(data.traceStageLabel || "Enquiry / order");
    setCompletenessPercent(Number(data.completenessPercent) || 0);
    setEntryStarted(Boolean(data.entryStarted));
    setEntryComplete(Boolean(data.entryComplete));
    setMissingFields(Array.isArray(data.missingFields) ? data.missingFields : []);
    setReminderCount(Number(data.reminderCount) || 0);
    setPlacedAt(data.placedAt || data.createdAt || "");
    setCompletedAt(data.completedAt || "");
    setElapsedDays(Number(data.elapsedDays) || 0);
    setAllLinesCompleted(Boolean(data.allLinesCompleted));
    setClientConfirmationLabel(data.clientConfirmationLabel || "");
  };

  useEffect(() => {
    if (!canUseOrders(role)) {
      history.replace("/dashboard");
      return;
    }
    if (isNew && isSuperAdmin) {
      history.replace("/orders");
      return;
    }
    if (isNew) {
      setLoading(false);
      return;
    }
    if (!orderId) {
      history.replace("/orders");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getOrder(orderId)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(res?.message || "Unable to load order");
          return;
        }
        applyOrder(unwrapGeoPayload(res) || {});
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load order");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, isNew, isSuperAdmin, role, history]);

  useEffect(() => {
    if (isView || !canWrite) {
      return;
    }
    const q = customerName.trim();
    if (q.length < 2) {
      setCustomerMatches([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setCustomerSearching(true);
      searchOrderCustomers(q)
        .then((res: any) => {
          if (cancelled) {
            return;
          }
          const rows = Array.isArray(res?.data) ? res.data : [];
          setCustomerMatches(
            rows.map((row: Record<string, unknown>) => mapSearchRowToCustomer(row)).filter(Boolean) as OrderCustomerOption[]
          );
        })
        .catch(() => {
          if (!cancelled) {
            setCustomerMatches([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setCustomerSearching(false);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [customerName, isView, canWrite]);

  const payload = () => ({
    customerName: customerName.trim() || null,
    customerPhone: customerPhone.trim() || null,
    customerEmail: customerEmail.trim() || null,
    partyOrganizationId: partyOrganizationId.trim() || null,
    productDesign: productDesign.trim() || null,
    productId: productId.trim() || null,
    productName: productName.trim() || null,
    dueDate: dueDate ? `${dueDate}T00:00:00` : null,
    specifications: specifications.trim() || null,
    workflowStatus,
    stuckReason: stuckReason.trim() || null,
    customStageLabel: customStageLabel.trim() || null,
    lines: lines
      .filter((line) => line.colour.trim() || line.quantity.trim())
      .map((line) => ({
        id: line.id,
        colour: line.colour.trim() || null,
        quantity: line.quantity.trim() ? Number(line.quantity) : null,
        workflowStatus: line.workflowStatus,
        stuckReason: line.stuckReason.trim() || null,
        customStageLabel: line.customStageLabel.trim() || null,
      })),
    documents,
  });

  const validateLines = () => {
    for (const line of lines) {
      if (line.quantity.trim() && (!Number.isFinite(Number(line.quantity)) || Number(line.quantity) < 1)) {
        return "Each colour quantity must be at least 1";
      }
      if (line.workflowStatus === "STUCK" && !line.stuckReason.trim()) {
        return "Enter why this colour line is stuck";
      }
      if (line.workflowStatus === "OTHER" && !line.customStageLabel.trim()) {
        return "Enter the custom stage for this colour line";
      }
    }
    if (workflowStatus === "STUCK" && !stuckReason.trim()) {
      return "Enter why this order is stuck";
    }
    if (workflowStatus === "OTHER" && !customStageLabel.trim()) {
      return "Enter the custom stage";
    }
    return "";
  };

  const handleSave = async (overrides?: { workflowStatus?: string }) => {
    if (!canWrite || isView) {
      return;
    }
    const invalid = validateLines();
    if (invalid) {
      notifications({ title: "Error!", message: invalid, type: "danger" });
      return;
    }
    if (overrides?.workflowStatus) {
      setWorkflowStatus(overrides.workflowStatus);
    }
    setSaving(true);
    try {
      const body = payload();
      if (overrides?.workflowStatus) {
        body.workflowStatus = overrides.workflowStatus;
        body.stuckReason = null;
        body.customStageLabel = null;
      }
      const res: any = isNew
        ? await createOrder(body)
        : await updateOrder(orderId as string, body);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not save this order",
          type: "danger",
        });
        return;
      }
      const data = unwrapGeoPayload(res) || {};
      notifications({
        title: "Success!",
        message: isNew ? "Order recorded" : "Order updated",
        type: "success",
      });
      if (isNew && (data.id || data._id)) {
        setConfirmOrderId(String(data.id || data._id));
        setConfirmEmailDraft(customerEmail.trim());
        setConfirmOpen(true);
        return;
      }
      applyOrder(data);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not save this order",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const openCreatedOrder = (id: string) => {
    setConfirmOpen(false);
    history.replace(`/orders/${id}/edit`);
  };

  const sendCreatedConfirmation = async () => {
    const email = confirmEmailDraft.trim();
    if (!confirmOrderId) {
      return;
    }
    if (!email) {
      notifications({
        title: "Error!",
        message: "Add the client's email before sending the confirmation",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      if (email !== customerEmail.trim()) {
        const body = payload();
        body.customerEmail = email;
        const updated: any = await updateOrder(confirmOrderId, body);
        if (!updated?.success) {
          notifications({
            title: "Error!",
            message: updated?.message || "Could not save the client email",
            type: "danger",
          });
          return;
        }
      }
      const sent: any = await sendOrderConfirmation(confirmOrderId);
      if (!sent?.success) {
        notifications({
          title: "Error!",
          message: sent?.message || "Could not send the confirmation",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Confirmation sent",
        type: "success",
      });
      openCreatedOrder(confirmOrderId);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not send the confirmation",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDocument = async (file?: File) => {
    if (!file || isView || !canWrite) {
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadOrderDocument(file);
      setDocuments((current) => [...current, uploaded]);
      notifications({
        title: "Success!",
        message: "File attached",
        type: "success",
      });
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload the file",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const applyCustomer = (option: OrderCustomerOption) => {
    setCustomerName(option.customerName);
    setCustomerPhone(option.customerPhone || customerPhone);
    setCustomerEmail(option.customerEmail || customerEmail);
    setPartyOrganizationId(option.partyOrganizationId);
    setCustomerMenuOpen(false);
  };

  const createCustomerParty = () => {
    setPartyOrganizationId("");
    setCustomerMenuOpen(false);
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setRecording(false);
  };

  const startAudioNote = async () => {
    if (isView || !canWrite || recording) {
      return;
    }
    const mime = pickAudioRecorderMime();
    if (!mime || typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      notifications({
        title: "Error!",
        message: "This browser cannot record audio notes",
        type: "danger",
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        recorderRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size < 1) {
          return;
        }
        const ext = mime.includes("mp4") ? "m4a" : mime.includes("mpeg") ? "mp3" : mime.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `client-note-${Date.now()}.${ext}`, { type: mime });
        await handleDocument(file);
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not start the microphone",
        type: "danger",
      });
    }
  };

  const handleAddNote = async () => {
    if (!canWrite || isNew || !orderId) {
      return;
    }
    const message = noteDraft.trim();
    if (!message) {
      notifications({
        title: "Error!",
        message: "Enter the conversation note",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const res: any = await addOrderNote(orderId, message);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not add the note",
          type: "danger",
        });
        return;
      }
      setNoteDraft("");
      applyOrder(unwrapGeoPayload(res) || {});
      notifications({
        title: "Success!",
        message: "Conversation noted",
        type: "success",
      });
    } catch {
      notifications({
        title: "Error!",
        message: "Could not add the note",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canWrite || isNew || !orderId || deleteInFlight.current) {
      return;
    }
    deleteInFlight.current = true;
    setSaving(true);
    try {
      const res: any = await deleteOrder(orderId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: "Could not remove this order. Please try again.",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Order removed",
        type: "success",
      });
      setConfirmDelete(false);
      history.push("/orders");
    } catch {
      notifications({
        title: "Error!",
        message: "Could not delete this order",
        type: "danger",
      });
    } finally {
      deleteInFlight.current = false;
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!canWrite || isNew || !orderId) {
      return;
    }
    setSaving(true);
    try {
      const res: any = await duplicateOrder(orderId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not duplicate this order",
          type: "danger",
        });
        return;
      }
      const copy = unwrapGeoPayload(res) || {};
      notifications({
        title: "Success!",
        message: `Duplicated as ${copy.orderCode || "a new order"}`,
        type: "success",
      });
      history.push(`/orders/${copy.id || copy._id}/edit`);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not duplicate this order",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNotify = async () => {
    if (!canWrite || isNew || !orderId) {
      return;
    }
    if (!customerEmail.trim()) {
      document.getElementById("orderEmail")?.focus();
      notifications({
        title: "Error!",
        message: "Add the client's email before notifying the client",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const res: any = await notifyOrderClient(orderId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not email the client",
          type: "danger",
        });
        return;
      }
      applyOrder(unwrapGeoPayload(res) || {});
      notifications({
        title: "Success!",
        message: "Client notified",
        type: "success",
      });
    } catch {
      notifications({
        title: "Error!",
        message: "Could not email the client",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMatchSearch = async () => {
    const q = matchQuery.trim();
    if (!q) {
      setMatchResults([]);
      return;
    }
    setMatching(true);
    try {
      const res: any = await searchOrderProducts(q);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not search products",
          type: "danger",
        });
        return;
      }
      const payload = unwrapGeoPayload(res);
      setMatchResults(Array.isArray(payload) ? payload : Array.isArray(res?.data) ? res.data : []);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not search products",
        type: "danger",
      });
    } finally {
      setMatching(false);
    }
  };

  const persistOrderStatus = async (
    next: string,
    extras?: { stuckReason?: string | null; customStageLabel?: string | null }
  ) => {
    if (!canWrite) {
      return;
    }
    const nextStuck = extras && "stuckReason" in extras ? extras.stuckReason || "" : stuckReason;
    const nextCustom =
      extras && "customStageLabel" in extras ? extras.customStageLabel || "" : customStageLabel;
    if (next === "STUCK" && !String(nextStuck).trim()) {
      setWorkflowStatus(next);
      return;
    }
    if (next === "OTHER" && !String(nextCustom).trim()) {
      setWorkflowStatus(next);
      return;
    }
    setWorkflowStatus(next);
    setStuckReason(next === "STUCK" ? String(nextStuck) : "");
    setCustomStageLabel(next === "OTHER" ? String(nextCustom) : "");
    if (isNew || !orderId) {
      return;
    }
    setSaving(true);
    try {
      const res: any = await updateOrderStatus(orderId, {
        workflowStatus: next,
        stuckReason: next === "STUCK" ? String(nextStuck).trim() : null,
        customStageLabel: next === "OTHER" ? String(nextCustom).trim() : null,
      });
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not update status",
          type: "danger",
        });
        return;
      }
      const data = unwrapGeoPayload(res) || {};
      setWorkflowStatus(data.workflowStatus || next);
      setStuckReason(data.stuckReason || "");
      setCustomStageLabel(data.customStageLabel || "");
      setCompletedAt(data.completedAt || "");
      setElapsedDays(Number(data.elapsedDays) || 0);
      setAllLinesCompleted(Boolean(data.allLinesCompleted));
      setOrderVersion((v) => v + 1);
      notifications({
        title: "Success!",
        message: `Status: ${data.workflowStatusLabel || workflowLabel(next, String(nextCustom))}`,
        type: "success",
      });
    } catch {
      notifications({
        title: "Error!",
        message: "Could not update status",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const mappedLines = (source: ColourLine[]) =>
    source
      .filter((line) => line.colour.trim() || line.quantity.trim())
      .map((line) => ({
        id: line.id,
        colour: line.colour.trim() || null,
        quantity: line.quantity.trim() ? Number(line.quantity) : null,
        workflowStatus: line.workflowStatus,
        stuckReason: line.stuckReason.trim() || null,
        customStageLabel: line.customStageLabel.trim() || null,
      }));

  const saveProduction = async (nextLines: ColourLine[]) => {
    if (!canWrite || isNew || !orderId) {
      return;
    }
    for (const line of nextLines) {
      if (line.workflowStatus === "STUCK" && !line.stuckReason.trim()) {
        return;
      }
      if (line.workflowStatus === "OTHER" && !line.customStageLabel.trim()) {
        return;
      }
    }
    setSaving(true);
    try {
      const res: any = await updateOrder(orderId, {
        ...payload(),
        lines: mappedLines(nextLines),
      });
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not update production",
          type: "danger",
        });
        return;
      }
      const data = unwrapGeoPayload(res) || {};
      setLines(linesFromOrder(data));
      setAllLinesCompleted(Boolean(data.allLinesCompleted));
      setOrderVersion((v) => v + 1);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not update production",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (id: string, patch: Partial<ColourLine>, persistProduction = false) => {
    const next = lines.map((line) => (line.id === id ? { ...line, ...patch } : line));
    setLines(next);
    if (persistProduction) {
      void saveProduction(next);
    }
  };

  if (loading) {
    return <div className="gi-registry-page">Loading order…</div>;
  }

  if (error) {
    return (
      <div className="gi-registry-page">
        <div className="gi-registry-toolbar">
          <h2>Orders</h2>
          <Button className="ResetBtn" onClick={() => history.push("/orders")}>
            Back to list
          </Button>
        </div>
        <div className="gi-form-banner">{error}</div>
      </div>
    );
  }

  const title = isNew ? "Record order" : isView ? "Order" : "Edit order";
  const recordReadOnly = isView || !canWrite;
  const sideReadOnly = !canWrite;
  const fillClass = entryComplete
    ? "order-completeness__fill is-complete"
    : entryStarted
    ? "order-completeness__fill is-started"
    : "order-completeness__fill";
  const showCompletePrompt =
    !isNew && allLinesCompleted && workflowStatus !== "COMPLETED" && canWrite;

  return (
    <div className="gi-registry-page">
      <HKPConfirmDialog
        open={confirmDelete}
        title="Remove order?"
        description={`Remove order ${orderCode || ""} from this studio?`}
        confirmLabel="Remove order"
        busy={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
      <Modal isOpen={confirmOpen} toggle={saving ? undefined : () => confirmOrderId && openCreatedOrder(confirmOrderId)} centered>
        <ModalHeader toggle={saving ? undefined : () => confirmOrderId && openCreatedOrder(confirmOrderId)}>
          Send a confirmation to the client?
        </ModalHeader>
        <ModalBody>
          <p>
            The email asks the client to read the order and confirm the details. If they do not,
            the order is confirmed automatically after 48 hours.
          </p>
          <Label htmlFor="confirmEmail">Customer email</Label>
          <Input
            id="confirmEmail"
            type="email"
            value={confirmEmailDraft}
            onChange={(e) => setConfirmEmailDraft(e.target.value)}
            maxLength={80}
          />
        </ModalBody>
        <ModalFooter>
          <Button type="button" outline disabled={saving} onClick={() => confirmOrderId && openCreatedOrder(confirmOrderId)}>
            Don't send
          </Button>
          <Button type="button" color="primary" disabled={saving || !confirmEmailDraft.trim()} onClick={sendCreatedConfirmation}>
            {saving ? "Sending…" : "Send confirmation"}
          </Button>
        </ModalFooter>
      </Modal>
      <div className="gi-registry-toolbar">
        <h2>{title}</h2>
        <div className="gi-form-actions" style={{ marginTop: 0 }}>
          <Button className="btn-hastkala-outline" onClick={() => history.push("/orders")}>
            Back to list
          </Button>
          {isView && canWrite && orderId ? (
            <Button className="ResetBtn" onClick={() => history.push(`/orders/${orderId}/edit`)}>
              Edit
            </Button>
          ) : null}
          {!isView && canWrite ? (
            <Button className="ResetBtn" disabled={saving} onClick={() => handleSave()}>
              {saving ? "Saving…" : isNew ? "Save order" : "Save changes"}
            </Button>
          ) : null}
          {!isNew && canWrite ? (
            <Button className="btn-hastkala-outline" disabled={saving} onClick={handleDuplicate}>
              Duplicate
            </Button>
          ) : null}
          {!isNew && canWrite ? (
            <Button className="btn-hastkala-outline" disabled={saving} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          ) : null}
        </div>
      </div>
      <p className="gi-au-hint">
        Capture whatever you have now. Filling these details is not the same as completing the
        order — mark Completed / sent when it has been finished and dispatched.
        {isSuperAdmin && orgName ? ` Studio: ${orgName}.` : ""}
      </p>
      {!isNew ? (
        <>
          <p className="order-trace">
            Order ID <strong>{orderCode || "—"}</strong>
            {" · "}
            Status: {workflowLabel(workflowStatus, customStageLabel)}
            {" · "}
            Placed {placedAt ? moment(new Date(placedAt)).format("YYYY-MM-DD") : "—"}
            {" · "}
            {completedAt
              ? `Sent ${moment(new Date(completedAt)).format("YYYY-MM-DD")}`
              : `${elapsedDays} day${elapsedDays === 1 ? "" : "s"} elapsed`}
            {" · "}
            Traceability stage: {traceStageLabel}
            {reminderCount > 0 ? ` · Reminders sent: ${reminderCount}` : ""}
            {clientConfirmationLabel ? ` · ${clientConfirmationLabel}` : ""}
          </p>
          <p className="gi-au-hint">
            Status is the production step for this order. Traceability stage is the
            authenticity chain (enquiry, production, sale) and is updated separately.
          </p>
          <div className="order-completeness">
            <div className="order-completeness__bar" aria-hidden="true">
              <div className={fillClass} style={{ width: `${completenessPercent}%` }} />
            </div>
            <span>
              {entryComplete
                ? "Record details filled"
                : entryStarted
                ? `Record ${completenessPercent}% filled`
                : "Record details incomplete"}
            </span>
          </div>
          {!entryComplete && missingFields.length > 0 ? (
            <div className="gi-form-banner is-info">
              Still needed: {missingFields.join(", ")}.
            </div>
          ) : null}
          {showCompletePrompt ? (
            <div className="gi-form-banner is-info">
              Every colour line is completed. Mark this order completed / sent?
              <Button
                className="ResetBtn"
                style={{ marginLeft: 12 }}
                disabled={saving}
                onClick={() => persistOrderStatus("COMPLETED")}
              >
                Mark completed / sent
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="gi-form-banner is-info">
          An order ID is assigned when you save. Conversation notes can be added after the first
          save.
        </div>
      )}
      <div className="gi-form-layout">
        <div className="gi-form-fields gi-au-form">
          <Label htmlFor="orderCustomer">Customer</Label>
          <p className="gi-au-hint">
            Search parties (person or business). If they are not listed, keep the name — saving
            the order creates a Customer party.
          </p>
          <div className="order-customer-search">
            <Input
              id="orderCustomer"
              value={customerName}
              autoComplete="off"
              disabled={recordReadOnly}
              onChange={(e) => {
                setCustomerName(e.target.value);
                setPartyOrganizationId("");
                setCustomerMenuOpen(true);
              }}
              onFocus={() => setCustomerMenuOpen(true)}
              placeholder="Client, buyer, or business name"
              maxLength={200}
            />
            {recordReadOnly || !customerMenuOpen || customerName.trim().length < 2 ? null : (
              <ul className="order-customer-search__menu" role="listbox">
                {customerSearching ? <li className="order-customer-search__meta">Searching parties…</li> : null}
                {customerMatches.map((option) => (
                  <li key={`${option.source}-${option.partyOrganizationId || option.customerName}`}>
                    <button
                      type="button"
                      className="order-customer-search__option"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyCustomer(option)}
                    >
                      <span>{option.customerName}</span>
                      <span className="order-customer-search__kind">
                        {option.kind === "business" ? "Business" : "Individual"}
                        {option.customerPhone ? ` · ${option.customerPhone}` : ""}
                      </span>
                    </button>
                  </li>
                ))}
                {shouldOfferCreateParty(customerName, customerMatches) ? (
                  <li>
                    <button
                      type="button"
                      className="order-customer-search__option"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={createCustomerParty}
                    >
                      Create party: {customerName.trim()}
                    </button>
                  </li>
                ) : null}
                {!customerSearching && customerMatches.length === 0 && !shouldOfferCreateParty(customerName, customerMatches) ? (
                  <li className="order-customer-search__meta">No matching parties</li>
                ) : null}
              </ul>
            )}
          </div>
          <Label htmlFor="orderPhone">Customer phone</Label>
          <Input
            id="orderPhone"
            value={customerPhone}
            disabled={recordReadOnly}
            onChange={(e) => setCustomerPhone(e.target.value)}
            maxLength={80}
          />
          <Label htmlFor="orderEmail">Customer email</Label>
          <Input
            id="orderEmail"
            type="email"
            value={customerEmail}
            disabled={recordReadOnly}
            onChange={(e) => setCustomerEmail(e.target.value)}
            maxLength={80}
          />
          <Label htmlFor="orderDesign">Product / design</Label>
          <Input
            id="orderDesign"
            value={productDesign}
            disabled={recordReadOnly}
            onChange={(e) => setProductDesign(e.target.value)}
            placeholder="What was ordered"
            maxLength={300}
          />
          <Label htmlFor="orderDue">Due date</Label>
          <Input
            id="orderDue"
            type="date"
            value={dueDate}
            disabled={recordReadOnly}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <Label htmlFor="orderSpecs">Specifications</Label>
          <Input
            id="orderSpecs"
            type="textarea"
            rows={4}
            value={specifications}
            disabled={recordReadOnly}
            onChange={(e) => setSpecifications(e.target.value)}
            placeholder="Size, weave, border, yarn, finishing, or other client specs"
            maxLength={4000}
          />
          <Label>Colour lines</Label>
          <p className="gi-au-hint">
            One line per colour or motif colourway. Production status for each colour is on the
            right. Completing every line does not close the order until you mark it completed /
            sent.
          </p>
          <div className="order-lines">
            {lines.map((line) => (
              <div className="order-line" key={line.id}>
                <Input
                  value={line.colour}
                  disabled={recordReadOnly}
                  placeholder="Colour"
                  maxLength={120}
                  onChange={(e) => updateLine(line.id, { colour: e.target.value })}
                />
                <Input
                  type="number"
                  min={1}
                  value={line.quantity}
                  disabled={recordReadOnly}
                  placeholder="Qty"
                  onChange={(e) => updateLine(line.id, { quantity: e.target.value })}
                />
                {recordReadOnly ? null : (
                  <Button
                    className="btn-hastkala-outline"
                    disabled={lines.length === 1}
                    onClick={() => setLines((current) => current.filter((item) => item.id !== line.id))}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
          {recordReadOnly ? null : (
            <div className="gi-form-actions">
              <Button className="btn-hastkala-outline" onClick={() => setLines((current) => [...current, newColourLine()])}>
                Add colour line
              </Button>
            </div>
          )}
          {!isNew ? (
            <>
              <Label>Product record</Label>
              <p className="gi-au-hint">
                Optional. Start a product from this order, or match an existing studio product by
                name. Certification still happens on the product.
              </p>
              {productId ? (
                <p className="order-trace">
                  Matched product: <strong>{productName || "Linked product"}</strong>
                  {recordReadOnly ? null : (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => {
                          setProductId("");
                          setProductName("");
                        }}
                      >
                        Clear
                      </button>
                    </>
                  )}
                </p>
              ) : (
                <p className="gi-au-hint">No product matched yet.</p>
              )}
              {recordReadOnly || !canWrite ? null : (
                <div className="gi-form-actions">
                  <Button
                    className="ResetBtn"
                    onClick={() =>
                      history.push(
                        `/createProduct?orderId=${encodeURIComponent(orderId || "")}&orderCode=${encodeURIComponent(orderCode || "")}`
                      )
                    }
                  >
                    Start product
                  </Button>
                </div>
              )}
              {recordReadOnly ? null : (
                <>
                  <Label htmlFor="orderMatch">Match existing product</Label>
                  <Input
                    id="orderMatch"
                    value={matchQuery}
                    onChange={(e) => setMatchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleMatchSearch();
                      }
                    }}
                    placeholder="Search by product name"
                  />
                  <div className="gi-form-actions">
                    <Button className="btn-hastkala-outline" disabled={matching} onClick={handleMatchSearch}>
                      {matching ? "Searching…" : "Search products"}
                    </Button>
                  </div>
                  {matchResults.length > 0 ? (
                    <div className="order-match-grid">
                      {matchResults.map((item) => (
                        <div className="geo-card" key={item.id}>
                          {item.imageUrl ? (
                            <img className="geo-card__photo" src={item.imageUrl} alt={item.name || "Product"} />
                          ) : (
                            <div className="geo-card__photo is-empty">No image</div>
                          )}
                          <div className="order-match-grid__name">{item.name || "Untitled product"}</div>
                          <Button
                            className="btn-hastkala-outline"
                            onClick={() => {
                              setProductId(item.id);
                              setProductName(item.name || "");
                              setMatchResults([]);
                            }}
                          >
                            Match
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </>
          ) : null}
          <Label>Audio notes</Label>
          <p className="gi-au-hint">
            Record what the client said (sizes, colours, delivery, other instructions). Saved
            with this order. Save the order to keep the note.
          </p>
          {recordReadOnly ? null : (
            <div className="gi-form-actions">
              {recording ? (
                <Button className="ResetBtn" type="button" onClick={stopRecording}>
                  Stop recording
                </Button>
              ) : (
                <Button className="btn-hastkala-outline" type="button" disabled={saving} onClick={startAudioNote}>
                  Record audio note
                </Button>
              )}
            </div>
          )}
          {documents.filter((doc) => isOrderAudioNote(doc.contentType, doc.fileName)).length > 0 ? (
            <ul className="order-doc-list">
              {documents
                .filter((doc) => isOrderAudioNote(doc.contentType, doc.fileName))
                .map((doc) => (
                  <li key={doc.url}>
                    <audio controls src={doc.url} preload="none">
                      <a href={doc.url} target="_blank" rel="noreferrer">
                        {doc.fileName || "Audio note"}
                      </a>
                    </audio>
                    {recordReadOnly ? null : (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() =>
                          setDocuments((current) => current.filter((item) => item.url !== doc.url))
                        }
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
            </ul>
          ) : (
            <p className="gi-au-hint">No audio notes yet.</p>
          )}
          <Label htmlFor="orderFiles">Documents</Label>
          {recordReadOnly ? null : (
            <Input
              id="orderFiles"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/png,application/pdf"
              disabled={saving}
              onChange={(e) => {
                handleDocument(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          )}
          <p className="gi-au-hint">JPEG, PNG, PDF, or Word. 12 MB or smaller. Save the order to keep attachments.</p>
          {documents.filter((doc) => !isOrderAudioNote(doc.contentType, doc.fileName)).length > 0 ? (
            <ul className="order-doc-list">
              {documents
                .filter((doc) => !isOrderAudioNote(doc.contentType, doc.fileName))
                .map((doc) => (
                  <li key={doc.url}>
                    <a href={doc.url} target="_blank" rel="noreferrer">
                      {doc.fileName || "Attached file"}
                    </a>
                    {recordReadOnly ? null : (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() =>
                            setDocuments((current) => current.filter((item) => item.url !== doc.url))
                          }
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </li>
                ))}
            </ul>
          ) : (
            <p className="gi-au-hint">No files attached yet.</p>
          )}
        </div>
        <div className="gi-form-fields order-form-side">
          {/* Mounted after the first load, so the panel's first request already has the loaded order's version.
              The panel shows itself to studio staff only: the API answers 403 to anyone else (the 401 page). */}
          {!isNew && orderId && orderVersion > 0 ? (
            <OrderApprovalPanel
              orderId={orderId}
              canWrite={canWrite}
              customerPhone={customerPhone}
              refreshKey={String(orderVersion)}
              role={role}
              documents={savedDocuments.filter((doc) => !isOrderAudioNote(doc.contentType, doc.fileName))}
              advancePayment={advancePayment}
              onAdvanceChange={setAdvancePayment}
            />
          ) : null}
          {!isNew && orderId ? <OrderMemosPanel orderId={orderId} role={role} /> : null}
          <div className="geo-card">
            <Label htmlFor="orderStatus">Status</Label>
            <p className="gi-au-hint">
              Change it here or from the order list. Filling the record is not the same as
              completing the order.
            </p>
            <div className="order-status-align">
              <Input
                id="orderStatus"
                type="select"
                value={workflowStatus}
                disabled={sideReadOnly}
                onChange={(e) => persistOrderStatus(e.target.value)}
              >
                {WORKFLOW_STATUSES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Input>
              {workflowStatus === "STUCK" ? (
                <Input
                  id="orderStuck"
                  value={stuckReason}
                  disabled={sideReadOnly}
                  onChange={(e) => setStuckReason(e.target.value)}
                  onBlur={(e) => persistOrderStatus("STUCK", { stuckReason: e.target.value })}
                  placeholder="Why work is blocked"
                  maxLength={400}
                  aria-label="Stuck reason"
                />
              ) : null}
              {workflowStatus === "OTHER" ? (
                <Input
                  id="orderStage"
                  value={customStageLabel}
                  disabled={sideReadOnly}
                  onChange={(e) => setCustomStageLabel(e.target.value)}
                  onBlur={(e) => persistOrderStatus("OTHER", { customStageLabel: e.target.value })}
                  placeholder="Name this stage"
                  maxLength={80}
                  aria-label="Custom stage"
                />
              ) : null}
            </div>
          </div>
          <div className="geo-card">
            <Label>Production updates</Label>
            <p className="gi-au-hint">
              Status per colourway. Save colour names and quantities on the left.
            </p>
            <div className="order-lines">
              {lines.map((line) => (
                <div className="order-line order-line--production" key={`prod-${line.id}`}>
                  <div className="order-line__label">
                    {line.colour.trim() || "Colour line"}
                    {line.quantity.trim() ? ` · ${line.quantity}` : ""}
                  </div>
                  <Input
                    type="select"
                    value={line.workflowStatus}
                    disabled={sideReadOnly}
                    onChange={(e) => updateLine(line.id, { workflowStatus: e.target.value }, true)}
                  >
                    {WORKFLOW_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Input>
                  {line.workflowStatus === "STUCK" ? (
                    <Input
                      className="order-line__extra"
                      value={line.stuckReason}
                      disabled={sideReadOnly}
                      placeholder="Why this colourway is stuck"
                      maxLength={400}
                      onChange={(e) => updateLine(line.id, { stuckReason: e.target.value })}
                      onBlur={(e) => updateLine(line.id, { stuckReason: e.target.value }, true)}
                    />
                  ) : null}
                  {line.workflowStatus === "OTHER" ? (
                    <Input
                      className="order-line__extra"
                      value={line.customStageLabel}
                      disabled={sideReadOnly}
                      placeholder="Custom stage for this colourway"
                      maxLength={80}
                      onChange={(e) => updateLine(line.id, { customStageLabel: e.target.value })}
                      onBlur={(e) => updateLine(line.id, { customStageLabel: e.target.value }, true)}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="geo-card">
            <Label htmlFor="orderNote">Client conversation</Label>
            {communications.length > 0 ? (
              <ul className="order-note-list">
                {communications.map((note, index) => (
                  <li key={note.id || index}>
                    <div className="order-note-meta">
                      {note.authorName || "Studio"}
                      {note.createdAt
                        ? ` · ${moment(new Date(note.createdAt)).format("YYYY-MM-DD HH:mm")}`
                        : ""}
                    </div>
                    <div>{note.message}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="gi-au-hint">No conversation recorded yet.</p>
            )}
            {isNew ? (
              <p className="gi-au-hint">Save the order first, then add conversation notes.</p>
            ) : sideReadOnly ? null : (
              <>
                <Input
                  id="orderNote"
                  type="textarea"
                  rows={3}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="What the client asked, confirmed, or changed"
                  maxLength={2000}
                />
                <div className="gi-form-actions">
                  <Button className="btn-hastkala-outline" disabled={saving} onClick={handleAddNote}>
                    Add note
                  </Button>
                  <Button className="ResetBtn" disabled={saving} onClick={handleNotify}>
                    Notify client
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
